import { ReactNode } from "react";

/**
 * Pedagogical callout boxes — the visual furniture that turns a lesson page
 * from "wall of text" into "teacher-style explainer".
 *
 *   <Tip>          quick musical advice
 *   <Mistake>      common student error
 *   <DidYouKnow>   historical / scientific context
 *   <TryThis>      actionable practice box
 *   <Pull>         pull-quote from the lesson
 *   <Stat>         single-number highlight
 *   <Compare>      A vs B side-by-side
 *   <Steps>        numbered practice progression
 *   <KeyIdea>      core concept callout
 *   <Anatomy>      labelled SVG breakdown
 */

type ChildrenProps = { children: ReactNode };
type IconCalloutProps = { children: ReactNode; title?: string; icon?: ReactNode };

/* ---------------------------------------------------------------- TIP */
export function Tip({ children, title = "Pro tip" }: IconCalloutProps) {
  return (
    <aside className="my-4 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4">
      <div className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-amber-300">
        <span className="text-base">💡</span>
        {title}
      </div>
      <div className="text-[14px] leading-relaxed text-nsm-ink">{children}</div>
    </aside>
  );
}

/* ----------------------------------------------------------- MISTAKE */
export function Mistake({ children, title = "Common mistake" }: IconCalloutProps) {
  return (
    <aside className="my-4 rounded-2xl border border-rose-500/30 bg-rose-500/5 p-4">
      <div className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-rose-300">
        <span className="text-base">⚠️</span>
        {title}
      </div>
      <div className="text-[14px] leading-relaxed text-nsm-ink">{children}</div>
    </aside>
  );
}

/* --------------------------------------------------------- DID YOU KNOW */
export function DidYouKnow({ children, title = "Did you know?" }: IconCalloutProps) {
  return (
    <aside className="my-4 rounded-2xl border border-violet-500/30 bg-violet-500/5 p-4">
      <div className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-violet-300">
        <span className="text-base">📚</span>
        {title}
      </div>
      <div className="text-[14px] leading-relaxed text-nsm-ink">{children}</div>
    </aside>
  );
}

/* --------------------------------------------------------------- TRY THIS */
export function TryThis({
  children,
  title = "Try this now",
  duration,
}: IconCalloutProps & { duration?: string }) {
  return (
    <aside className="my-4 rounded-2xl border-2 border-emerald-500/40 bg-emerald-500/5 p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-emerald-300">
          <span className="text-base">▶︎</span>
          {title}
        </div>
        {duration && (
          <span className="rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-200">
            {duration}
          </span>
        )}
      </div>
      <div className="text-[14px] leading-relaxed text-nsm-ink">{children}</div>
    </aside>
  );
}

/* --------------------------------------------------------------- KEY IDEA */
export function KeyIdea({ children, title = "Key idea" }: IconCalloutProps) {
  return (
    <aside className="my-4 rounded-2xl border-l-4 border-amber-400 bg-amber-500/10 p-4">
      <div className="mb-1 text-xs font-bold uppercase tracking-[0.18em] text-amber-300">{title}</div>
      <div className="text-[15px] font-medium leading-relaxed text-nsm-ink">{children}</div>
    </aside>
  );
}

/* ------------------------------------------------------------------ PULL */
export function Pull({ children, by }: { children: ReactNode; by?: string }) {
  return (
    <blockquote className="my-6 border-l-4 border-amber-400/60 pl-4">
      <div className="font-display text-xl italic leading-snug text-nsm-ink">"{children}"</div>
      {by && <div className="mt-2 text-xs uppercase tracking-wider text-nsm-dim">— {by}</div>}
    </blockquote>
  );
}

/* ------------------------------------------------------------------ STAT */
export function Stat({
  value,
  label,
  unit,
  color = "amber",
}: {
  value: string | number;
  label: string;
  unit?: string;
  color?: "amber" | "emerald" | "rose" | "blue" | "violet";
}) {
  const colorMap: Record<string, string> = {
    amber: "text-amber-300 border-amber-500/30 bg-amber-500/5",
    emerald: "text-emerald-300 border-emerald-500/30 bg-emerald-500/5",
    rose: "text-rose-300 border-rose-500/30 bg-rose-500/5",
    blue: "text-blue-300 border-blue-500/30 bg-blue-500/5",
    violet: "text-violet-300 border-violet-500/30 bg-violet-500/5",
  };
  return (
    <div className={`rounded-2xl border ${colorMap[color]} px-5 py-4`}>
      <div className="font-display text-3xl font-bold leading-none">
        {value}
        {unit && <span className="ml-1 text-base text-nsm-dim">{unit}</span>}
      </div>
      <div className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-nsm-dim">{label}</div>
    </div>
  );
}

/* ----------------------------------------------------------- STAT ROW */
export function StatRow({ children }: ChildrenProps) {
  return <div className="my-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">{children}</div>;
}

/* ---------------------------------------------------------------- COMPARE */
export function Compare({
  left,
  right,
  leftLabel,
  rightLabel,
}: {
  left: ReactNode;
  right: ReactNode;
  leftLabel: string;
  rightLabel: string;
}) {
  return (
    <div className="my-4 grid grid-cols-1 gap-3 md:grid-cols-2">
      <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-4">
        <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-rose-300">{leftLabel}</div>
        <div className="text-[14px] leading-relaxed text-nsm-ink">{left}</div>
      </div>
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
        <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-emerald-300">{rightLabel}</div>
        <div className="text-[14px] leading-relaxed text-nsm-ink">{right}</div>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- STEPS */
export function Steps({ children, title }: { children: ReactNode; title?: string }) {
  return (
    <div className="my-4 rounded-2xl border border-nsm-border bg-nsm-card/40 p-4">
      {title && (
        <div className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-amber-300">{title}</div>
      )}
      <ol className="space-y-2 text-[14px] leading-relaxed text-nsm-ink [counter-reset:step]">
        {children}
      </ol>
    </div>
  );
}

export function Step({ children }: ChildrenProps) {
  return (
    <li className="flex gap-3 [counter-increment:step] before:flex before:h-6 before:w-6 before:flex-shrink-0 before:items-center before:justify-center before:rounded-full before:bg-amber-500/20 before:font-display before:text-xs before:font-bold before:text-amber-300 before:content-[counter(step)]">
      <span>{children}</span>
    </li>
  );
}

/* ------------------------------------------------------ ANATOMY DIAGRAM */
export function Anatomy({
  title,
  children,
  caption,
}: {
  title: string;
  children: ReactNode;
  caption?: string;
}) {
  return (
    <figure className="my-6 rounded-2xl border border-nsm-border bg-nsm-card/40 p-5">
      <div className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-amber-300">{title}</div>
      <div className="flex items-center justify-center">{children}</div>
      {caption && (
        <figcaption className="mt-3 text-center text-[12px] italic text-nsm-dim">{caption}</figcaption>
      )}
    </figure>
  );
}

/* ---------------------------------------------------- VOCABULARY BOX */
export function Vocab({ children, title = "Words to know" }: IconCalloutProps) {
  return (
    <aside className="my-4 rounded-2xl border border-blue-500/30 bg-blue-500/5 p-4">
      <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-blue-300">
        <span className="text-base">📖</span>
        {title}
      </div>
      <div className="space-y-2 text-[14px] leading-relaxed text-nsm-ink">{children}</div>
    </aside>
  );
}

export function Term({ name, def }: { name: string; def: ReactNode }) {
  return (
    <div>
      <span className="font-display font-semibold text-nsm-ink">{name}</span>
      <span className="text-nsm-dim"> — {def}</span>
    </div>
  );
}

/* ----------------------------------------------- KEY HIGHLIGHTS */
export function KeyHighlights({
  children,
  title = "Key highlights",
}: {
  children: ReactNode;
  title?: string;
}) {
  return (
    <aside className="my-6 rounded-2xl border border-amber-500/40 bg-gradient-to-br from-amber-500/10 via-transparent to-amber-500/5 p-5">
      <div className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-amber-300">
        <span className="text-base">★</span>
        {title}
      </div>
      <ul className="space-y-2 text-[14px] leading-relaxed text-nsm-ink [counter-reset:hl]">
        {children}
      </ul>
    </aside>
  );
}

export function Highlight({ children }: ChildrenProps) {
  return (
    <li className="flex gap-3 [counter-increment:hl] before:flex before:h-6 before:w-6 before:flex-shrink-0 before:items-center before:justify-center before:rounded-full before:bg-amber-400/20 before:font-display before:text-xs before:font-bold before:text-amber-200 before:content-[counter(hl)]">
      <span>{children}</span>
    </li>
  );
}

/* ---------------------------------------------- LESSON LINK CALLOUT */
export function NextUp({
  to,
  label,
  why,
}: {
  to: string;
  label: string;
  why: string;
}) {
  // Inside the lesson site this page sits beside one video. Following the link
  // would swap the companion to the next lesson while that video stays put (and
  // drop the embed view), so the lesson site's own lesson list does the moving.
  if (typeof window !== "undefined" && window.parent !== window) return null;
  return (
    <aside className="my-6 rounded-2xl border border-cyan-500/30 bg-cyan-500/5 p-4">
      <div className="mb-1 text-xs font-bold uppercase tracking-[0.18em] text-cyan-300">Up next</div>
      <a href={to} className="block">
        <div className="font-display text-lg font-bold text-nsm-ink hover:underline">{label} →</div>
        <div className="mt-1 text-[13px] text-nsm-dim">{why}</div>
      </a>
    </aside>
  );
}
